import React from 'react';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import { Place as PlaceIcon } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import RsvpToggle from './RsvpToggle';
import {
  formatHomeDate,
  formatHomeTime,
  getRelativeDayLabel,
  getSpecialGameTypeLabel,
  parseHomeDate,
} from './homeAgenda';

const displayOnlyChipProps = { size: 'small', clickable: false, onClick: () => { } };

const formatRecord = (record) => (record ? `${record.wins}-${record.draws}-${record.losses}` : null);

const TeamLabel = ({ name, slug, colorHex, record }) => (
  <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
    {colorHex && (
      <Box
        data-testid="jersey-swatch"
        sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: colorHex, border: 1, borderColor: 'divider', flexShrink: 0 }}
      />
    )}
    <Typography
      variant="h6"
      component="span"
      sx={{ fontWeight: 600, fontSize: { xs: '1.05rem', sm: '1.25rem' }, wordBreak: 'break-word' }}
    >
      {slug ? (
        <Box component={RouterLink} to={`/teams/${slug}`} sx={{ color: 'text.primary', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
          {name}
        </Box>
      ) : name}
    </Typography>
    {formatRecord(record) && (
      <Typography variant="caption" color="text.secondary">{formatRecord(record)}</Typography>
    )}
  </Stack>
);

/** Prominent card for the signed-in member's next player game. */
const NextGameHero = ({ game, rsvp, onRsvp, canRsvp, today }) => {
  const start = parseHomeDate(game.gameStart);
  const specialType = getSpecialGameTypeLabel(game.gameType);
  const location = [game.venueName, game.fieldName].filter(Boolean).join(' · ');
  const attending = rsvp?.attending ?? game.isAttending ?? null;

  return (
    <Box
      data-testid="next-game-hero"
      sx={{ bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1, overflow: 'hidden', mb: 3 }}
    >
      <Box
        sx={(theme) => ({
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
          color: 'common.white',
          px: { xs: 2, sm: 2.5 },
          py: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 1.5,
          flexWrap: 'wrap',
        })}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="overline" component="p" sx={{ opacity: 0.85, lineHeight: 1.5, wordBreak: 'break-word' }}>
            Next game ·{' '}
            {game.leagueSlug ? (
              <Box component={RouterLink} to={`/leagues/${game.leagueSlug}/standings`} sx={{ color: 'inherit' }}>
                {game.leagueName}
              </Box>
            ) : game.leagueName}
          </Typography>
          <Typography variant="h5" component="p" sx={{ fontWeight: 600, fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
            {formatHomeDate(start)} · {formatHomeTime(start)}
          </Typography>
        </Box>
        <Chip
          label={getRelativeDayLabel(start, today)}
          {...displayOnlyChipProps}
          sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: 'common.white' }}
        />
      </Box>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        sx={{ px: { xs: 2, sm: 2.5 }, py: 2 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <TeamLabel
              name={game.playingForTeamName}
              slug={game.playingForTeamSlug}
              colorHex={game.playingForJerseyColorHex}
              record={game.playingForRecord}
            />
            {game.playingAgainstTeamName ? (
              <>
                <Typography color="text.secondary" variant="body2">vs</Typography>
                <TeamLabel
                  name={game.playingAgainstTeamName}
                  slug={game.playingAgainstTeamSlug}
                  colorHex={game.playingAgainstJerseyColorHex}
                  record={game.playingAgainstRecord}
                />
              </>
            ) : null}
            {specialType && <Chip label={specialType} color="warning" variant="outlined" {...displayOnlyChipProps} />}
          </Stack>
          {location && (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.75 }}>
              <PlaceIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">{location}</Typography>
            </Stack>
          )}
        </Box>

        {canRsvp ? (
          <RsvpToggle
            value={attending}
            disabled={!!rsvp?.submitting}
            error={rsvp?.error}
            onChange={onRsvp}
            ariaLabel={`Attendance for ${game.leagueName} next game`}
          />
        ) : game.availabilityLink ? (
          <Button component={RouterLink} to={game.availabilityLink} variant="outlined" size="small">
            Set availability
          </Button>
        ) : null}
      </Stack>
    </Box>
  );
};

export default NextGameHero;
